<template>
    <header class="flex flex-col py-4">
        <div
            class="flex flex-col md:flex-row md:items-center md:justify-between py-4 lg:flex-none"
        >
            <h3
                class="text-md font-medium mb-5 md:mb-0 leading-6 text-gray-900"
            >
                <span class="block md:inline">
                    {{ currentViewLabel }}
                </span>
                <LastUpdated />
            </h3>
            <div class="flex justify-between items-center">
                <div
                    class="relative flex items-center rounded-md bg-white shadow-sm md:items-stretch"
                >
                    <div
                        class="pointer-events-none absolute inset-0 rounded-md ring-1 ring-inset ring-gray-300"
                        aria-hidden="true"
                    ></div>
                    <!-- prev button -->
                    <button
                        type="button"
                        class="flex items-center justify-center rounded-l-md py-2 pl-3 pr-4 text-gray-400 md:w-9 md:px-2 md:hover:bg-gray-50 hover:text-gray-500 hover:bg-slate-200 focus:outline-none focus:relative disabled:bg-gray-200 active:ring-1 active:ring-inset active:ring-slate-200"
                        @click="handlePrev()"
                        :disabled="!canPrev"
                        tabindex="0"
                    >
                        <span class="sr-only">{{ prevBtnLabel }}</span>
                        <svg
                            class="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                                clip-rule="evenodd"
                            />
                        </svg>
                    </button>
                    <span
                        class="px-3.5 text-sm font-semibold text-gray-900 flex items-center justify-center w-32"
                    >
                        {{ navButtonsLabel }}
                    </span>
                    <span
                        class="relative -mx-px h-5 w-px bg-gray-300 md:hidden"
                    ></span>
                    <!-- next button -->
                    <button
                        type="button"
                        class="flex items-center justify-center rounded-r-md py-2 pl-3 pr-4 text-gray-400 md:w-9 md:px-2 md:hover:bg-gray-50 hover:text-gray-500 hover:bg-slate-200 focus:outline-none focus:relative disabled:bg-gray-200 active:ring-1 active:ring-inset active:ring-slate-200"
                        @click="handleNext()"
                        :disabled="!canNext"
                        tabindex="0"
                    >
                        <span class="sr-only">{{ nextBtnLabel }}</span>
                        <svg
                            class="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                                clip-rule="evenodd"
                            />
                        </svg>
                    </button>
                </div>
                <div class="ml-4 flex items-center">
                    <button
                        type="button"
                        class="flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        :aria-expanded="showViewDropdown"
                        aria-haspopup="true"
                        @click="resetDates()"
                        v-if="view === 'list-range'"
                    >
                        Show All Availability
                    </button>
                    <div class="relative" v-if="view !== 'list-range'">
                        <button
                            type="button"
                            class="shadow font-medium text-sm rounded items-center gap-2 inline-flex w-40 h-9 pl-4 pr-3 py-2 text-slate-800 justify-start border border-slate-300 bg-white whitespace-nowrap ui-open:bg-slate-200 relative hover:bg-slate-200 active:border active:border-slate-300 focus:outline-none focus:border focus:border-slate-300"
                            :aria-expanded="showViewDropdown"
                            aria-haspopup="true"
                            @click="toggleView()"
                        >
                            {{ currentView?.label }} view
                            <span class="px-1.5 absolute right-2">
                                <i class="fa-solid fa-chevron-down fa-xs"></i>
                            </span>
                        </button>
                        <transition name="fade">
                            <div
                                class="w-60 absolute right-0 bottom-50 overflow-auto rounded-md bg-white text-base shadow-sm border border-slate-200 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm p-3 mt-2 z-10"
                                role="menu"
                                aria-orientation="vertical"
                                aria-labelledby="menu-button"
                                tabindex="-1"
                                v-if="showViewDropdown"
                            >
                                <div class="py-1" role="none">
                                    <a
                                        v-for="(view, vIndex) in activeViews"
                                        href="#"
                                        class="block w-full text-gray-900 relative select-none py-2 pl-3 pr-9 hover:bg-gray-100 rounded-md cursor-pointer mb-0.5"
                                        :class="{
                                            '!bg-gray-100':
                                                view === currentView,
                                        }"
                                        role="menuitem"
                                        tabindex="-1"
                                        id="menu-item-0"
                                        :key="'view' + vIndex"
                                        @click.prevent="setView(view)"
                                    >
                                        {{ view.label }} view
                                    </a>
                                </div>
                            </div>
                        </transition>
                    </div>
                </div>
            </div>
        </div>
    </header>
</template>
<script>
import { mapState, mapActions } from 'pinia';
import { useCalendarStore } from '@/store/calendar';
import LastUpdated from '@/components/calendar/partials/last-updated/last-updated.vue';
import dayjs from 'dayjs';

export default {
    name: 'CalendarControls',
    data: () => ({}),
    components: {
        LastUpdated,
    },
    computed: {
        ...mapState(useCalendarStore, [
            'loading',
            'showViewDropdown',
            'view',
            'views',
            'today',
            'currentView',
            'activeDate',
            'chosenDate',
            'activeYear',
            'activeMonth',
            'activeDay',
            'activeYear',
            'currentViewLabel',
        ]),
        activeViews() {
            return this.views.filter((view) => !view.hidden);
        },
        canPrev() {
            if (this.view === 'month') {
                const prevDate = this.activeDate.subtract(1, 'month');
                if (prevDate.isBefore(this.today.startOf('month'))) {
                    return false;
                }
            }
            if (this.view === 'list') {
                const prevDate = this.activeDate.subtract(1, 'month');
                if (prevDate.isBefore(this.today.startOf('month'))) {
                    return false;
                }
            }
            if (this.view === 'day') {
                const prevDate = this.activeDate.subtract(1, 'day');
                if (prevDate.isBefore(this.today)) {
                    return false;
                }
            }
            if (this.view === 'year') {
                const prevDate = this.activeDate.subtract(1, 'year');
                if (prevDate.isBefore(this.today)) {
                    return false;
                }
            }
            return true;
        },
        canNext() {
            const nextDate = dayjs.duration(this.activeDate.diff(this.today));
            if (nextDate.years() >= 1) {
                return false;
            }
            return true;
        },
        navButtonsLabel() {
            let label = this.activeMonth.full;
            switch (this.view) {
                case 'year':
                    label = this.activeYear.full;
                    break;
                case 'month':
                    label = this.activeMonth.full;
                    break;
                case 'day':
                    label =
                        this.today === this.chosenDate
                            ? 'Today'
                            : this.activeMonth.short +
                              ' ' +
                              this.activeDay.num +
                              ', ' +
                              this.activeYear.full;
                    break;
                case 'list':
                    label = this.activeMonth.full;
                    break;
            }
            return label;
        },
        nextBtnLabel() {
            let label = 'Next Month';
            switch (this.view) {
                case 'year':
                    label = 'Next Year';
                    break;
                case 'month':
                    label = 'Next Month';
                    break;
                case 'day':
                    label = 'Next Day';
                    break;
                case 'list':
                    label = 'Next Month';
                    break;
            }
            return label;
        },
        prevBtnLabel() {
            let label = 'Prev Month';
            switch (this.view) {
                case 'year':
                    label = 'Prev Year';
                    break;
                case 'month':
                    label = 'Prev Month';
                    break;
                case 'day':
                    label = 'Prev Day';
                    break;
                case 'list':
                    label = 'Prev Month';
                    break;
            }
            return label;
        },
    },
    methods: {
        ...mapActions(useCalendarStore, [
            'setLoading',
            'setShowViewDropdown',
            'setViewDropdown',
            'setDateRange',
            'setCheckInRange',
            'getEvents',
            'setView',
            'setToday',
            'prevMonth',
            'nextMonth',
            'nextYear',
            'prevYear',
            'nextDay',
            'prevDay',
        ]),
        toggleView() {
            let viewShow = !this.showViewDropdown;
            this.setShowViewDropdown(viewShow);
        },
        resetDates() {
            this.setView({ name: 'month' });
            /*this.setDateRange({
                startDate: null,
                endDate: null,
            });*/
            this.setCheckInRange({
                checkIn: null,
                checkOut: null,
            });
            this.getEvents();
            this.$router.push(
                `/hotels/properties/${this.$route.params.slug}`,
                true
            );
        },
        handlePrev() {
            switch (this.view) {
                case 'year':
                    this.prevYear();
                    break;
                case 'month':
                    this.prevMonth();
                    break;
                case 'day':
                    this.prevDay();
                    break;
                case 'list':
                    this.prevMonth();
                    break;
            }
        },
        handleNext() {
            switch (this.view) {
                case 'year':
                    this.nextYear();
                    break;
                case 'month':
                    this.nextMonth();
                    break;
                case 'day':
                    this.nextDay();
                    break;
                case 'list':
                    this.nextMonth();
                    break;
            }
        },
    },
    mounted() {
        let viewportWidth = window.innerWidth;
        const mobileWidth = 768;

        if (viewportWidth < mobileWidth) {
            this.setViewDropdown('mobile');
        } else {
            this.setViewDropdown('desktop');
        }

        window.addEventListener('resize', () => {
            viewportWidth = window.innerWidth;
            if (viewportWidth < mobileWidth) {
                this.setViewDropdown('mobile');
            } else {
                this.setViewDropdown('desktop');
            }
        });
    },
};
</script>
